import React, { useState, useMemo } from "react";
import PropTypes from "prop-types";
import { Modal, Button, Card, Icon, Loader, Input, Divider } from "semantic-ui-react";
import { useGetUserCommunities } from "../../hooks";

export const CommunitySelectionModal = ({
  open,
  onClose,
  onCommunitySelect,
}) => {
  const [page, setPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState("");
  const size = 8;

  const { data, isLoading, refetch } = useGetUserCommunities(page, size);

  const communities = data?.hits?.hits || [];
  const total = data?.hits?.total || 0;
  const totalPages = Math.ceil(total / size);

  const filteredCommunities = useMemo(() => {
    if (!searchQuery.trim()) return communities;
    const query = searchQuery.toLowerCase();
    return communities.filter((c) =>
      c.metadata?.title?.toLowerCase().includes(query) ||
      c.slug?.toLowerCase().includes(query)
    );
  }, [communities, searchQuery]);

  const handleSelect = (communityId, communityTitle) => {
    onCommunitySelect(communityId, communityTitle);
  };

  const handlePageChange = (newPage) => {
    setPage(newPage);
    refetch();
  };

  const handleSearchChange = (_, { value }) => {
    setSearchQuery(value);
    setPage(1);
  };

  return (
    <Modal open={open} onClose={onClose} size="medium" closeIcon>
      <Modal.Header content="Select Community for Review" />
      <Modal.Content scrolling>
        {isLoading ? (
          <Loader inline centered active size="small" />
        ) : communities.length === 0 ? (
          <div style={{ textAlign: "center", padding: "2rem 0", color: "#999" }}>
            <Icon name="users" size="large" verticalAlign="middle" style={{ marginBottom: "0.5rem" }} />
            <p>You are not a member of any communities.</p>
          </div>
        ) : (
          <>
            <Input
              fluid
              icon="search"
              placeholder="Search communities..."
              value={searchQuery}
              onChange={handleSearchChange}
              style={{ marginBottom: "1rem" }}
            />

            <Card.Group doubling itemsPerRow={1} styled>
              {filteredCommunities.map((community) => (
                <Card
                  key={community.id}
                  onClick={() =>
                    handleSelect(
                      community.id,
                      community.metadata?.title || "Untitled"
                    )
                  }
                  style={{
                    cursor: "pointer",
                    transition: "all 0.2s ease",
                    border: "1px solid #e0e0e0",
                    borderRadius: "8px"
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.boxShadow = "0 4px 12px rgba(0,0,0,0.1)";
                    e.currentTarget.style.borderColor = "#2185d0";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.boxShadow = "none";
                    e.currentTarget.style.borderColor = "#e0e0e0";
                  }}
                >
                  <Card.Content>
                    <Card.Header style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                      <Icon name="folder open" color="blue" />
                      {community.metadata?.title || "Untitled"}
                    </Card.Header>
                    {community.slug && (
                      <Card.Meta>
                        <Icon name="link" size="small" />
                        <span style={{ marginLeft: "0.25rem" }}>{community.slug}</span>
                      </Card.Meta>
                    )}
                  </Card.Content>
                  <Card.Content extra>
                    <Button size="tiny" floated="right" icon>
                      <Icon name="arrow right" />
                    </Button>
                    <span style={{ color: "#999", fontSize: "0.85em" }}>Click to select</span>
                  </Card.Content>
                </Card>
              ))}
            </Card.Group>

            {filteredCommunities.length === 0 && searchQuery && (
              <div style={{ textAlign: "center", padding: "2rem 0", color: "#999" }}>
                <Icon name="search" size="large" />
                <p>No communities match your search.</p>
              </div>
            )}

            {totalPages > 1 && (
              <Divider />
            )}

            {totalPages > 1 && (
              <div style={{ display: "flex", justifyContent: "center", alignItems: "center", gap: "0.75rem" }}>
                <Button
                  basic
                  size="small"
                  disabled={page === 1}
                  onClick={() => handlePageChange(page - 1)}
                  icon
                >
                  <Icon name="angle left" />
                </Button>
                <span style={{ fontSize: "0.9em", color: "#666" }}>
                  Page <strong>{page}</strong> of {totalPages}
                </span>
                <Button
                  basic
                  size="small"
                  disabled={page >= totalPages}
                  onClick={() => handlePageChange(page + 1)}
                  icon
                >
                  <Icon name="angle right" />
                </Button>
              </div>
            )}
          </>
        )}
      </Modal.Content>
      <Modal.Actions>
        <Button onClick={onClose} basic>
          Cancel
        </Button>
      </Modal.Actions>
    </Modal>
  );
};

CommunitySelectionModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onCommunitySelect: PropTypes.func.isRequired,
};
